import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Info, Lightbulb, ShieldAlert, TriangleAlert } from 'lucide-react';
import type { AdmonitionKind, AdocBlock, AdocInline } from '@/lib/asciidoc';
import { adocStringContent, parseAsciiDoc } from '@/lib/asciidoc';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { normalizeWikiTitle } from '@/lib/wikiSlug';
import { cn } from '@/lib/utils';

interface AsciiDocContentProps {
  /** Raw AsciiDoc markup (a wiki article's or publication section's `content` field). */
  content: string;
  className?: string;
  /** Renders the document title (`= Title`) as an `<h1>`. Off by default since the surrounding page usually already shows a title. */
  showTitle?: boolean;
}

/**
 * Renders AsciiDoc content — used for NIP-54 wiki articles and NKBIP-01
 * publication sections written in AsciiDoc rather than Djot (AsciiDoc is
 * the format the NKBIP-01 specification itself is written in, and is
 * common for wiki content ported from other AsciiDoc-based systems).
 *
 * Security: this NEVER uses `dangerouslySetInnerHTML`. Every node maps to
 * a concrete React element with auto-escaped text. AsciiDoc's raw-HTML
 * passthrough features (`pass:[...]`, `+++...+++`, `[html]` blocks) are
 * NOT implemented by the parser at all, so there's no HTML string to leak
 * through. Every link/image destination goes through `sanitizeUrl()`.
 */
export function AsciiDocContent({ content, className, showTitle }: AsciiDocContentProps) {
  const doc = parseAsciiDoc(content);

  return (
    <div className={cn('break-words', className)}>
      {showTitle && doc.title && <h1 className="mb-4 text-3xl font-bold tracking-tight break-words">{doc.title}</h1>}
      {doc.children.map((block, i) => (
        <Fragment key={i}>{renderBlock(block)}</Fragment>
      ))}
    </div>
  );
}

const admonitionStyles: Record<AdmonitionKind, { icon: typeof Info; className: string; label: string }> = {
  note: { icon: Info, className: 'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950/20 dark:text-blue-300', label: 'Note' },
  tip: { icon: Lightbulb, className: 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-300', label: 'Tip' },
  important: { icon: ShieldAlert, className: 'border-purple-300 bg-purple-50 text-purple-900 dark:border-purple-800 dark:bg-purple-950/20 dark:text-purple-300', label: 'Important' },
  warning: { icon: TriangleAlert, className: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-300', label: 'Warning' },
  caution: { icon: AlertTriangle, className: 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/20 dark:text-red-300', label: 'Caution' },
};

function renderBlocks(blocks: AdocBlock[]): ReactNode {
  return blocks.map((block, i) => <Fragment key={i}>{renderBlock(block)}</Fragment>);
}

function renderBlock(block: AdocBlock): ReactNode {
  switch (block.tag) {
    case 'heading': {
      const level = Math.min(Math.max(block.level, 1), 6);
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      const sizes: Record<number, string> = {
        1: 'text-3xl font-bold mt-8 mb-4',
        2: 'text-2xl font-bold mt-8 mb-3 border-b pb-2',
        3: 'text-xl font-semibold mt-6 mb-3',
        4: 'text-lg font-semibold mt-5 mb-2',
        5: 'text-base font-semibold mt-4 mb-2',
        6: 'text-sm font-semibold mt-4 mb-2 text-muted-foreground',
      };
      return (
        <Tag id={block.id} className={sizes[level]}>
          {renderInlines(block.children)}
        </Tag>
      );
    }

    case 'paragraph':
      return <p className="mb-4 leading-7">{renderInlines(block.children)}</p>;

    case 'thematic_break':
      return <hr className="my-6 border-border" />;

    case 'admonition': {
      const style = admonitionStyles[block.kind];
      const Icon = style.icon;
      return (
        <div className={`mb-4 flex gap-3 rounded-lg border p-4 ${style.className}`}>
          <Icon className="mt-0.5 size-5 shrink-0" />
          <div className="min-w-0 flex-1 [&>*:last-child]:mb-0">
            <p className="mb-1 font-semibold">{style.label}</p>
            {renderBlocks(block.children)}
          </div>
        </div>
      );
    }

    case 'quote':
      return (
        <figure className="mb-4">
          <blockquote className="border-l-4 border-border pl-4 italic text-muted-foreground [&>*:last-child]:mb-0">
            {renderBlocks(block.children)}
          </blockquote>
          {block.attribution && (
            <figcaption className="mt-1 pl-4 text-sm text-muted-foreground">— {block.attribution}</figcaption>
          )}
        </figure>
      );

    case 'code':
      return (
        <pre className="mb-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm">
          <code className={block.lang ? `language-${block.lang.replace(/[^\w-]/g, '')}` : undefined}>
            {block.text}
          </code>
        </pre>
      );

    case 'literal':
      return (
        <pre className="mb-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm whitespace-pre-wrap">{block.text}</pre>
      );

    case 'sidebar':
      return (
        <div className="mb-4 rounded-lg border border-border bg-muted/40 p-4 [&>*:last-child]:mb-0">
          {renderBlocks(block.children)}
        </div>
      );

    case 'bullet_list':
      return <ul className="mb-4 list-disc space-y-1 pl-6">{block.items.map((item, i) => <li key={i}>{renderInlines(item.children)}{item.sublist && renderBlock(item.sublist)}</li>)}</ul>;

    case 'ordered_list':
      return (
        <ol className="mb-4 list-decimal space-y-1 pl-6">
          {block.items.map((item, i) => (
            <li key={i}>
              {renderInlines(item.children)}
              {item.sublist && renderBlock(item.sublist)}
            </li>
          ))}
        </ol>
      );

    case 'table':
      return (
        <div className="mb-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className="border-b border-border">
                  {row.map((cell, j) =>
                    block.header && i === 0 ? (
                      <th key={j} className="p-2 text-left font-semibold">
                        {renderInlines(cell)}
                      </th>
                    ) : (
                      <td key={j} className="p-2">
                        {renderInlines(cell)}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'image_block': {
      const src = sanitizeUrl(block.src);
      if (!src) return block.alt ? <p className="mb-4 text-muted-foreground">[{block.alt}]</p> : null;
      return (
        <figure className="mb-4">
          <img src={src} alt={block.alt} className="mx-auto max-w-full rounded-lg" loading="lazy" />
          {block.title && <figcaption className="mt-1 text-center text-sm text-muted-foreground">{block.title}</figcaption>}
        </figure>
      );
    }

    default:
      return null;
  }
}

function renderInlines(nodes: AdocInline[]): ReactNode {
  return nodes.map((node, i) => <Fragment key={i}>{renderInline(node)}</Fragment>);
}

function renderInline(node: AdocInline): ReactNode {
  switch (node.tag) {
    case 'text':
      return node.text;

    case 'break':
      return <br />;

    case 'strong':
      return <strong>{renderInlines(node.children)}</strong>;

    case 'emphasis':
      return <em>{renderInlines(node.children)}</em>;

    case 'monospace':
      return <code className="rounded bg-muted px-1.5 py-0.5 text-sm">{renderInlines(node.children)}</code>;

    case 'superscript':
      return <sup>{renderInlines(node.children)}</sup>;

    case 'subscript':
      return <sub>{renderInlines(node.children)}</sub>;

    case 'highlight':
      return <mark className="rounded bg-yellow-200/60 px-0.5 dark:bg-yellow-500/30">{renderInlines(node.children)}</mark>;

    case 'link': {
      const href = sanitizeUrl(node.href);
      const children = renderInlines(node.children);
      return href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:no-underline">
          {children}
        </a>
      ) : (
        <>{children}</>
      );
    }

    case 'image': {
      const src = sanitizeUrl(node.src);
      if (!src) return node.alt ? <span className="text-muted-foreground">[{node.alt}]</span> : null;
      return <img src={src} alt={node.alt} className="mx-auto my-4 max-w-full rounded-lg" loading="lazy" />;
    }

    case 'wikilink': {
      const slug = normalizeWikiTitle(node.target);
      const label = node.display || node.target;
      if (!slug) return <>{label}</>;
      return (
        <Link to={`/w/${encodeURIComponent(slug)}`} className="text-primary underline decoration-dotted underline-offset-2 hover:no-underline">
          {label}
        </Link>
      );
    }

    default:
      return null;
  }
}

/** Plain-text summary of AsciiDoc content (for search indexing / card previews). Exposed for reuse outside the renderer. */
export function asciiDocPlainText(content: string): string {
  return blockPlainText(parseAsciiDoc(content).children).trim();
}

function blockPlainText(blocks: AdocBlock[]): string {
  let out = '';
  for (const block of blocks) {
    switch (block.tag) {
      case 'heading':
      case 'paragraph':
        out += `${adocStringContent(block.children)} `;
        break;
      case 'admonition':
      case 'quote':
      case 'sidebar':
        out += `${blockPlainText(block.children)} `;
        break;
      case 'bullet_list':
      case 'ordered_list':
        for (const item of block.items) {
          out += `${adocStringContent(item.children)} `;
          if (item.sublist) out += `${blockPlainText([item.sublist])} `;
        }
        break;
      default:
        break;
    }
  }
  return out;
}
